import { useLayoutEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useMe } from './api/hooks'
import { hasBabyPhase, profileComplete } from './lib/metrics'
import { ageFrom } from './lib/body'
import { todayIso } from './lib/format'
import { ApiError } from './api/client'
import { TrackingTab } from './features/tracking/TrackingTab'
import { TimerTab } from './features/timer/TimerTab'
import { TimerProvider, BreakOverlay } from './features/timer/TimerProvider'
import { InstallHint } from './components/InstallHint'
import { RestTimerProvider, RestBar } from './features/workout/RestTimerProvider'
import { WeekTab } from './features/content/WeekTab'
import { NutritionTab } from './features/content/NutritionTab'
import { WorkoutTab } from './features/content/WorkoutTab'
import { ProgramTab } from './features/workout/ProgramTab'
import { DeskTab } from './features/content/DeskTab'
import { BabyTab } from './features/content/BabyTab'
import { ThemeToggle } from './components/ThemeToggle'
import { LoginScreen } from './features/auth/LoginScreen'
import { AdminTab } from './features/admin/AdminTab'
import { SettingsTab } from './features/settings/SettingsTab'
import { Onboarding } from './features/settings/Onboarding'
import { api } from './api/client'
import { clearPersistedCache } from './lib/cache'

const TABS = [
  { id: 'takip', label: 'Takip' },
  { id: 'program', label: 'Haftalık Program' },
  { id: 'beslenme', label: 'Beslenme & Porsiyon' },
  { id: 'programim', label: 'Programım' },
  { id: 'antrenman', label: 'Antrenman Rehberi' },
  { id: 'masa', label: 'Masa Başı' },
  { id: 'bebek', label: 'Bebek Planı' },
  { id: 'mola', label: 'Mola Sayacı' },
  { id: 'ayarlar', label: 'Ayarlar' },
  { id: 'yonetim', label: 'Kullanıcılar' },
] as const

type TabId = (typeof TABS)[number]['id']

/**
 * Sekme şeridi — kayan bir "thumb" ile.
 *
 * Seçili sekmenin zeminini doğrudan boyamak yerine tek bir hareketli katman
 * kullanıyoruz: geçiş sırasında göz nereden nereye gidildiğini takip ediyor.
 * Konum ölçümle geliyor (`useLayoutEffect` — boyamadan önce, yoksa ilk karede
 * thumb sıfır noktasından kayardı).
 *
 * Ölçümü CSS değişkeni olarak ŞERİDE yazıyoruz, her düğmeye değil: değişken
 * kalıtsaldır ve çocuk başına yazmak her düğmede stil yeniden hesabı demek.
 */
function Tabs({ tabs, active, onSelect }: {
  tabs: readonly { id: TabId; label: string }[]
  active: TabId
  onSelect: (id: TabId) => void
}) {
  const strip = useRef<HTMLElement>(null)
  const [ready, setReady] = useState(false)

  useLayoutEffect(() => {
    const el = strip.current
    if (!el) return
    const move = () => {
      const on = el.querySelector<HTMLElement>('[aria-selected="true"]')
      if (!on) return
      el.style.setProperty('--thumb-x', `${on.offsetLeft}px`)
      el.style.setProperty('--thumb-w', `${on.offsetWidth}px`)
      // Seçili sekme şeridin dışındaysa görünür alana getir (telefonda sık).
      on.scrollIntoView({ inline: 'nearest', block: 'nearest' })
    }
    move()
    // İlk ölçümden SONRA geçişi açıyoruz: açılışta thumb'ın soldan kayması
    // bir bilgi taşımıyor, sadece gürültü.
    const t = setTimeout(() => setReady(true), 60)
    const ro = new ResizeObserver(move)
    ro.observe(el)
    window.addEventListener('resize', move)
    return () => { clearTimeout(t); ro.disconnect(); window.removeEventListener('resize', move) }
  }, [active, tabs.length])

  return (
    <div className="tabsbar">
      <nav className="tabs" role="tablist" ref={strip} data-ready={ready}>
        {tabs.map((t) => (
          <button key={t.id} role="tab" aria-selected={active === t.id} onClick={() => onSelect(t.id)}>
            {t.label}
          </button>
        ))}
      </nav>
    </div>
  )
}

/**
 * Telefonda alt gezinme çubuğu.
 *
 * Üstteki kaydırmalı şerit telefonda çalışmıyordu: 390px ekranda aynı anda
 * bir buçuk sekme görünüyor, geri kalanına ulaşmak için yatay kaydırmak
 * gerekiyor ve o şerit sayfanın en üstünde — yani antrenman kaydederken
 * sekme değiştirmek "yukarı kaydır, sonra yana kaydır" demek.
 *
 * Alt barda dört sık kullanılan bölüm sabit duruyor, gerisi "Daha" ile
 * açılan bir levhada. Dört sayısı keyfi değil: beşinci sütun 390px'te
 * etiketleri kırpıyor.
 */
const PRIMARY: TabId[] = ['takip', 'programim', 'mola', 'ayarlar']

function BottomNav({ tabs, active, onSelect }: {
  tabs: readonly { id: TabId; label: string }[]
  active: TabId
  onSelect: (id: TabId) => void
}) {
  const [more, setMore] = useState(false)
  const bar = useRef<HTMLElement>(null)

  // Barın yüksekliği CSS'te sabit yazılamaz: güvenli alan (çentikli ekranlar),
  // kullanıcının yazı tipi ölçeği ve etiket sarması yüksekliği değiştiriyor.
  // Sabit 57px denendi ve levha ikonların üstüne bindi. Ölçüp yazıyoruz.
  useLayoutEffect(() => {
    const el = bar.current
    if (!el) return
    const set = () => document.documentElement.style.setProperty('--nav-h', `${el.offsetHeight}px`)
    set()
    const ro = new ResizeObserver(set)
    ro.observe(el)
    return () => ro.disconnect()
  }, [tabs.length])
  const primary = tabs.filter((t) => PRIMARY.includes(t.id))
  const rest = tabs.filter((t) => !PRIMARY.includes(t.id))
  const inRest = rest.some((t) => t.id === active)

  const go = (id: TabId) => { onSelect(id); setMore(false) }

  return (
    <>
      {/* Levha açıkken arka plan karartılıyor: dışarı dokunmak kapatıyor. */}
      <div className={`sheet-back${more ? ' on' : ''}`} onClick={() => setMore(false)} />

      <div className={`sheet${more ? ' on' : ''}`} role="menu" aria-hidden={!more}>
        <div className="sheet-grab" />
        {rest.map((t) => (
          <button key={t.id} role="menuitem" className={active === t.id ? 'primary' : ''}
                  onClick={() => go(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      <nav className="bottomnav" aria-label="Bölümler" ref={bar}>
        {primary.map((t) => (
          <button key={t.id} aria-current={active === t.id} onClick={() => go(t.id)}>
            <Icon id={t.id} />
            <span>{short(t.label)}</span>
          </button>
        ))}
        {rest.length > 0 && (
          <button aria-current={inRest} aria-expanded={more} onClick={() => setMore(!more)}>
            <Icon id="daha" />
            <span>Daha</span>
          </button>
        )}
      </nav>
    </>
  )
}

/** Alt barda uzun etiketler kırpılıyor — orada kısası yeterli. */
function short(label: string): string {
  if (label === 'Mola Sayacı') return 'Mola'
  if (label === 'Programım') return 'Program'
  return label
}

/**
 * İkonlar tek parça `stroke` yolları — ikon paketi kurmuyoruz. Beş ikon için
 * bir bağımlılık, hem kb hem de tema uyumu açısından pahalı bir tercih olurdu.
 */
function Icon({ id }: { id: string }) {
  const common = {
    width: 21, height: 21, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'currentColor', strokeWidth: 1.8,
    strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  }
  if (id === 'takip') return <svg {...common}><path d="M3 17l5-5 3.5 3.5L21 6" /><path d="M15 6h6v6" /></svg>
  if (id === 'programim') return <svg {...common}><path d="M6.5 8v8M17.5 8v8M3.5 10v4M20.5 10v4M6.5 12h11" /></svg>
  if (id === 'mola') return <svg {...common}><circle cx="12" cy="13" r="8" /><path d="M12 13V9M9 2h6" /></svg>
  if (id === 'ayarlar') return <svg {...common}><path d="M4 7h10M18 7h2M4 17h2M10 17h10" /><circle cx="16" cy="7" r="2" /><circle cx="8" cy="17" r="2" /></svg>
  return <svg {...common}><circle cx="5" cy="12" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="19" cy="12" r="1.4" /></svg>
}

export default function App() {
  const initial = (location.hash.replace('#', '') || 'takip') as TabId
  const [tab, setTab] = useState<TabId>(TABS.some((t) => t.id === initial) ? initial : 'takip')
  const me = useMe()
  const qc = useQueryClient()

  if (me.isLoading) {
    return <div className="center"><p className="sub">Yükleniyor…</p></div>
  }

  if (me.error instanceof ApiError && me.error.status === 401) {
    return <LoginScreen onSuccess={() => {
      // Başka bir hesabın önbelleği duruyor olabilir; geçersiz kılmak değil
      // TEMİZLEMEK gerekiyor, yoksa yeni kullanıcı bir an öncekinin verisini
      // görüyor (invalidate eski veriyi ekranda tutup arkadan yeniliyor).
      //
      // Temizledikten sonra YENİLİYORUZ: cache.clear() sorguyu kökünden
      // kaldırdığı için ekrandaki gözlemci yeniden istek atmıyordu ve giriş
      // başarılı olmasına rağmen uygulama "Kontrol ediliyor…" ekranında
      // takılı kalıyordu. Oturum çerezi zaten yazıldı, yenileme temiz açılış.
      qc.clear()
      clearPersistedCache()
      location.replace('/')
    }} />
  }

  if (me.error || !me.data) {
    return (
      <div className="center">
        <div className="card" style={{ maxWidth: 420 }}>
          <h2>Bağlanılamadı</h2>
          <p className="sub">
            Sunucuya ulaşılamıyor. İnternetin yoksa son kaydedilen veriler gösterilir;
            bağlantı gelince kendiliğinden güncellenir.
          </p>
          <button style={{ marginTop: 12 }} onClick={() => me.refetch()}>Tekrar dene</button>
        </div>
      </div>
    )
  }

  const p = me.data.profile

  // Kurulum tamamlanmadan panel açılmıyor: boy/kilo sıfırken hesaplanan her
  // sayı (VKİ, bazal metabolizma, hedef kalori) uydurma olurdu.
  if (!profileComplete(p)) {
    return <Onboarding profile={p} name={me.data.name} />
  }

  const select = (id: TabId) => {
    setTab(id)
    history.replaceState(null, '', `#${id}`)
  }

  // Yöneticiye özel bölümler. Beslenme ve Bebek Planı sana özel yazılmış
  // içerik: porsiyon örnekleri senin öğün düzenine, bebek planı senin
  // takvimine göre. Başka birinde yanlış bilgi olurlar, eksik değil.
  const adminOnly: TabId[] = ['yonetim', 'beslenme', 'bebek']

  const shown = TABS
    .filter((t) => !adminOnly.includes(t.id) || me.data!.isAdmin)
    // Bebek Planı ayrıca doğum tarihi girilmişse — yönetici bile olsan
    // tarih yoksa gösterecek bir şey yok.
    .filter((t) => t.id !== 'bebek' || hasBabyPhase(p))

  return (
    <TimerProvider profile={p}>
      <RestTimerProvider>
      <BreakOverlay />
      <RestBar />
      <div className="wrap">
      <header className="top">
        <div>
          <h1>Kilo Takip Paneli</h1>
          <p className="sub">
            {/* Sabit "Salı & Perşembe tenis" yazıyordu — panel artık birden fazla
                kişi tarafından kullanılıyor ve o cümle çoğu için yanlış.
                Telefonda satırın yarısı "…" ile kesiliyordu; kesik bir cümle
                bilgi taşımıyor, sadece bozuk duruyor. Fazlası orada gizleniyor. */}
            {p.startWeightKg} kg
            <span className="hide-sm">
              {' · '}{p.heightCm} cm ·{' '}
              {p.birthDate ? ageFrom(p.birthDate, todayIso()) : p.age} yaş ·{' '}
              öğünler {p.meal1.slice(0, 5)} &amp; {p.meal2.slice(0, 5)}
            </span>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span className="sub sub-email" style={{ fontSize: 12.5 }}>{me.data.email}</span>
          <ThemeToggle />
          <button onClick={async () => {
            // Sunucu oturumunu kapatmak YETMİYOR: kalıcı önbellek (localStorage)
            // "me" kaydını tutuyor ve sayfa yenilenince uygulama o kopyayla
            // açılıp çıkış yapılmamış gibi görünüyordu. Önce sunucuya haber
            // ver, sonra hem bellekteki hem diskteki kopyayı sil.
            try { await api.logout() } catch { /* ağ yoksa da yerelden çık */ }
            qc.clear()
            clearPersistedCache()
            // replace + kök: sekme hash'i de gitsin, geri tuşu panele dönmesin.
            location.replace('/')
          }}>Çıkış</button>
        </div>
      </header>

      <Tabs tabs={shown} active={tab} onSelect={select} />

      {/* iOS'ta kurulum çubuğu diye bir şey yok; yönergeyi biz gösteriyoruz.
          Bir kez kapatılınca dönmüyor. */}
      <InstallHint />

      {/* key={tab}: sekme değişince içerik yeniden monte oluyor ve giriş
          animasyonu (.tabpanel) her geçişte çalışıyor. */}
      <div className="tabpanel" key={tab}>
      {tab === 'takip' && <TrackingTab me={me.data} />}
      {tab === 'program' && <WeekTab profile={p} />}
      {tab === 'beslenme' && me.data.isAdmin && <NutritionTab profile={p} />}
      {tab === 'programim' && <ProgramTab profile={p} />}
      {tab === 'antrenman' && <WorkoutTab />}
      {tab === 'masa' && <DeskTab profile={p} />}
      {tab === 'bebek' && me.data.isAdmin && hasBabyPhase(p) && <BabyTab profile={p} />}
      {tab === 'mola' && <TimerTab profile={p} />}
      {tab === 'ayarlar' && <SettingsTab profile={p} isAdmin={me.data.isAdmin} />}
      {tab === 'yonetim' && <AdminTab />}
      </div>

        <footer>Veriler sunucuda saklanıyor · çevrimdışıyken son bilinen hal gösterilir</footer>
      </div>
      <BottomNav tabs={shown} active={tab} onSelect={select} />
      </RestTimerProvider>
    </TimerProvider>
  )
}
